import React from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
    DefaultButton,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SearchBox,
    Spinner,
    Stack,
    Text,
} from '@fluentui/react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import useCan from '../../permissions/ability';
import useSwitchOrganization from '../../hooks/useSwitchOrganization';
import InstancePickerDialog from '../../components/Settings/InstancePickerDialog';
import { setLastUsedInstance } from '../../store/settingsSlice';
import {
    AREAS,
    buildAreaUrl,
    findArea,
    findSetting,
    ISettingArea,
    SETTINGS,
} from '../../settings/registry';

interface IResolverParams {
    settingId?: string;
    areaKey?: string;
}

/** `?instance=first` — "any instance will do", for documentation that just shows where a setting is. */
export const FIRST_INSTANCE = 'first';

/** `?instance=2` — the second one in the list. 1-based, because the audience is people, not code. */
const INSTANCE_ORDINAL = /^[1-9][0-9]*$/;

/**
 * Reads `?instance=` as a position rather than an id: `first` is position 1, and a bare positive
 * integer is that position, 1-based. Returns a 0-based index, or undefined when the value is an
 * ordinary instance id.
 *
 * Only whole positive numbers count, so an id that merely starts with a digit is still treated as
 * an id. A instance whose id is exactly "2" would lose to the ordinal — ids here are generated, so
 * that stays theoretical.
 */
export const instanceOrdinalIndex = (value?: string): number | undefined => {
    if (value === FIRST_INSTANCE) return 0;
    if (value && INSTANCE_ORDINAL.test(value)) return Number(value) - 1;
    return undefined;
};

/**
 * Resolves `/go/setting/<id>` (and `/go/area/<key>`) to wherever that setting currently lives.
 *
 * Documentation links against the identifier rather than a URL, so screens can be re-tabbed, moved
 * behind a panel or renamed without breaking published links. Everything that can stop the journey
 * - wrong organization, missing permission, an instance the link did not name, an id that no longer
 * exists - is answered here rather than by dropping the reader on a blank page.
 */
/** Sentinel instance id for the merged picker's Global option. */
const GLOBAL_COUNTERPART_ID = '__global__';

const SettingDeepLinkResolver: React.FC<RouteComponentProps<IResolverParams>> = (props) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const switchOrganization = useSwitchOrganization();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const lastUsedInstances = useSelector((state: RootState) => state.settings.lastUsedInstances);

    const params = new URLSearchParams(props.location.search);
    const requestedOrganization = params.get('org') ?? undefined;
    const requestedInstance = params.get('instance') ?? undefined;

    const settingId = props.match.params.settingId;
    const setting = findSetting(settingId);
    const area: ISettingArea | undefined = props.match.params.areaKey
        ? findArea(props.match.params.areaKey)
        : findArea(setting?.area);

    const [switching, setSwitching] = React.useState(false);
    const [organizationSwitched, setOrganizationSwitched] = React.useState(false);
    const [cancelled, setCancelled] = React.useState(false);

    const needsOrganizationSwitch =
        !!requestedOrganization && !!selectedOrganization && requestedOrganization !== selectedOrganization;
    /*
     * Permissions arrive after the first render (authSlice starts with an empty list), and useCan()
     * builds its ability from that list — so on a cold load every gated area looks denied for a
     * moment. Rendering the denial then flashed 'you do not have permission' at people who did,
     * right before the page resolved. An empty list means NOT YET KNOWN, not 'denied'.
     */
    const knownPermissions = useSelector((state: RootState) => state.auth.permissions);
    const permissionsLoaded = knownPermissions.length > 0;
    // useCan is a hook, so it runs on every render; an area without a permission is open to all.
    const canAreaPermission = useCan(area?.permission ?? '');
    const permitted = !area?.permission || canAreaPermission;
    /*
     * `?instance=` can name a position instead of an id: `first`, or a 1-based ordinal like `2`.
     * Both mean "any of them will do, I am showing where this setting lives" — which storefront or
     * extension demonstrates it does not matter, and documentation cannot know a tenant's ids.
     *
     * Resolved through the picker rather than around it so it inherits everything the picker already
     * knows: the instanceFilter for the setting, loading and error states, and the fact that a single
     * instance is auto-selected anyway. It lands on a real instance id, so the URL it redirects to is
     * an ordinary link, not a second sentinel.
     *
     * An ordinal past the end of the list falls through to the picker rather than silently taking
     * the last one — asking is better than pretending the link named something.
     */
    const requestedOrdinal = instanceOrdinalIndex(requestedInstance);
    const instanceId = requestedOrdinal !== undefined ? undefined : requestedInstance ?? undefined;
    const needsInstance = !!area?.instanceType && !instanceId;

    const go = React.useCallback(
        (chosenInstance?: string) => {
            if (!area) return;
            const url = buildAreaUrl(area, chosenInstance ?? instanceId);
            if (!url) return;
            const [path, search] = url.split('?');
            const query = new URLSearchParams(search);
            if (setting) query.set('focus', setting.id);
            const queryString = query.toString();
            history.replace({ pathname: path, search: queryString ? `?${queryString}` : '' });
        },
        [area, history, instanceId, setting]
    );

    // Everything is known - go, without leaving /go in the history.
    React.useEffect(() => {
        if (!area || cancelled) return;
        if (needsOrganizationSwitch && !organizationSwitched) return;
        if (!permitted || needsInstance) return;
        go();
    }, [area, cancelled, needsOrganizationSwitch, organizationSwitched, permitted, needsInstance, go]);

    if (!area) {
        return <UnknownSettingScreen requestedId={settingId ?? props.match.params.areaKey} />;
    }

    if (needsOrganizationSwitch && !organizationSwitched) {
        return (
            <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { padding: '1em', maxWidth: 640 } }}>
                <h1>Switch organization?</h1>
                <MessageBar messageBarType={MessageBarType.warning}>
                    This link points at <strong>{area.label}</strong> in organization{' '}
                    <strong>{requestedOrganization}</strong>, but you are working in{' '}
                    <strong>{selectedOrganization}</strong>.
                </MessageBar>
                <Stack horizontal tokens={{ childrenGap: '1em' }}>
                    <PrimaryButton
                        text={switching ? 'Switching…' : `Switch to ${requestedOrganization}`}
                        disabled={switching}
                        onClick={async () => {
                            if (!requestedOrganization) return;
                            setSwitching(true);
                            try {
                                await switchOrganization(requestedOrganization);
                                setOrganizationSwitched(true);
                            } finally {
                                setSwitching(false);
                            }
                        }}
                    />
                    <DefaultButton
                        text={`Stay in ${selectedOrganization}`}
                        disabled={switching}
                        onClick={() => setOrganizationSwitched(true)}
                    />
                </Stack>
            </Stack>
        );
    }

    if (permissionsLoaded && !permitted) {
        return (
            <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { padding: '1em', maxWidth: 640 } }}>
                <h1>{setting?.label ?? area.label}</h1>
                <MessageBar messageBarType={MessageBarType.blocked}>
                    You do not have access to {area.label}. It requires the{' '}
                    <strong>{area.permission}</strong> permission - an administrator in your organization can grant
                    it.
                </MessageBar>
            </Stack>
        );
    }

    if (needsInstance && area.instanceType) {
        const suggestedId = lastUsedInstances?.[`${selectedOrganization ?? ''}:${area.instanceType}`];
        const counterpart = area.globalCounterpart ? AREAS.find((a) => a.key === area.globalCounterpart) : undefined;
        return (
            <>
                <Stack styles={{ root: { padding: '1em' } }}>
                    <Spinner label='Opening…' />
                </Stack>
                <InstancePickerDialog
                    instanceType={area.instanceType}
                    filter={setting?.instanceFilter}
                    settingLabel={setting?.label ?? area.label}
                    suggestedId={suggestedId}
                    autoSelectIndex={requestedOrdinal}
                    extraOptions={counterpart ? [{ id: GLOBAL_COUNTERPART_ID, label: 'Global', secondary: 'All storefronts, organization-wide' }] : undefined}
                    onSelect={(chosen) => {
                        if (chosen === GLOBAL_COUNTERPART_ID && counterpart) {
                            const url = buildAreaUrl(counterpart);
                            if (url) history.replace(url);
                            return;
                        }
                        dispatch(
                            setLastUsedInstance({
                                organization: selectedOrganization,
                                instanceType: area.instanceType as string,
                                id: chosen,
                            })
                        );
                        go(chosen);
                    }}
                    onCancel={() => {
                        setCancelled(true);
                        history.replace('/settings');
                    }}
                />
            </>
        );
    }

    return (
        <Stack styles={{ root: { padding: '1em' } }}>
            <Spinner label={`Opening ${setting?.label ?? area.label}…`} />
        </Stack>
    );
};

/** A link to a setting that no longer exists should say so, and help find the current one. */
const UnknownSettingScreen: React.FC<{ requestedId?: string }> = ({ requestedId }) => {
    const [search, setSearch] = React.useState(requestedId?.split('.').pop() ?? '');
    const term = search.trim().toLowerCase();
    const matches = term
        ? SETTINGS.filter(
              (s) => s.id.toLowerCase().includes(term) || s.label.toLowerCase().includes(term)
          ).slice(0, 25)
        : [];

    return (
        <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { padding: '1em', maxWidth: 720 } }}>
            <h1>That setting could not be found</h1>
            <MessageBar messageBarType={MessageBarType.warning}>
                Nothing in this application is registered as <strong>{requestedId}</strong>. The link may be out of
                date, or the setting may have been removed.
            </MessageBar>
            <SearchBox
                placeholder='Search settings by name…'
                value={search}
                onChange={(_, v) => setSearch(v ?? '')}
            />
            <Stack tokens={{ childrenGap: '0.5em' }}>
                {matches.map((match) => {
                    const area = findArea(match.area);
                    return (
                        <Stack key={match.id} horizontal tokens={{ childrenGap: '0.5em' }} verticalAlign='center'>
                            <Link href={`/go/setting/${encodeURIComponent(match.id)}`}>{match.label}</Link>
                            <Text variant='small' styles={{ root: { opacity: 0.75 } }}>
                                {area?.label ?? match.area}
                            </Text>
                        </Stack>
                    );
                })}
                {term && matches.length === 0 && <Text>No settings match "{search}".</Text>}
            </Stack>
            <Text variant='small'>
                {SETTINGS.length} settings across {AREAS.length} screens are registered.{' '}
                <Link href='/settings/reference'>Browse them all</Link>.
            </Text>
        </Stack>
    );
};

export default SettingDeepLinkResolver;
